import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Box,
  Button,
  Container,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isUserAdmin } from '../utils/adminUtils';
import {
  getPreinvoiceRecipients,
  PREINVOICE_DEFAULT_MESSAGE_TEMPLATE,
  PREINVOICE_STATUSES,
  PREINVOICE_TEMPLATE_PLACEHOLDERS,
  previewPreinvoiceMessage,
  sendPreinvoices,
  subscribeToRecentPreinvoices,
  updatePreinvoiceStatus
} from '../utils/preinvoiceUtils';

const INVOICE_TEST_ROUTES = [
  {
    title: 'Išankstinė sąskaita faktūra',
    description: 'Patikrai su testiniais pirkėjo duomenimis ir spausdinimo/PDF mygtuku.',
    to: '/admin/invoices/test-proforma',
    buttonLabel: 'Atidaryti testą',
  },
  {
    title: 'Sąskaita faktūra',
    description: 'Standartinė sąskaitos faktūros testinė forma su paslaugų eilutėmis.',
    to: '/admin/invoices/test-factura',
    buttonLabel: 'Atidaryti testą',
  },
  {
    title: 'Sąskaita faktūra (MB pirkėjas)',
    description: 'Alternatyvus testas MB pirkėjui be logotipo.',
    to: '/admin/invoices/test-coach-mb',
    buttonLabel: 'Atidaryti testą',
  },
];

export default function AdminSaskaitosPage() {
  const { currentUser } = useAuth();
  const isAdminUser = isUserAdmin(currentUser);
  const [open, setOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [amountEur, setAmountEur] = useState('40');
  const [isTest, setIsTest] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recentPreinvoices, setRecentPreinvoices] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [messageTemplate, setMessageTemplate] = useState(PREINVOICE_DEFAULT_MESSAGE_TEMPLATE);

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  useEffect(() => {
    if (!isAdminUser) {
      setRecentPreinvoices([]);
      return undefined;
    }
    const unsub = subscribeToRecentPreinvoices(currentYear, setRecentPreinvoices);
    return () => unsub();
  }, [currentYear, isAdminUser]);

  const loadRecipients = async () => {
    try {
      const list = await getPreinvoiceRecipients();
      setRecipients(list);
    } catch (err) {
      setError(err.message || 'Nepavyko įkelti klientų sąrašo.');
    }
  };

  const openModal = async () => {
    setError('');
    setRecipientSearch('');
    setOpen(true);
    if (recipients.length === 0) {
      await loadRecipients();
    }
  };

  const closeModal = () => {
    if (sending) return;
    setOpen(false);
    setMessageOpen(false);
    setConfirmOpen(false);
  };

  const goToMessageStep = () => {
    setError('');
    setOpen(false);
    setMessageOpen(true);
  };

  const goBackToSelectionStep = () => {
    if (sending) return;
    setMessageOpen(false);
    setOpen(true);
  };

  const toggleRecipient = (id) => {
    setSelectedRecipientIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const selectedRecipients = useMemo(() => {
    const byId = new Map(recipients.map((r) => [r.id, r]));
    return selectedRecipientIds
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [recipients, selectedRecipientIds]);

  const unselectedFilteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    const selectedIdSet = new Set(selectedRecipientIds);
    const unselected = recipients.filter((r) => !selectedIdSet.has(r.id));
    if (!q) return unselected;
    return unselected.filter((r) =>
      (r.fullName || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q)
    );
  }, [recipients, recipientSearch, selectedRecipientIds]);

  const canSubmit = Number(amountEur) > 0 && selectedRecipients.length > 0 && !sending;
  const canGoToMessage = Number(amountEur) > 0 && selectedRecipients.length > 0;

  const messagePreview = useMemo(() => {
    const firstRecipient = selectedRecipients[0];
    const previewVars = {
      vardas: firstRecipient?.fullName || 'Klientas',
      pdfUrl: '[PDF nuoroda bus sugeneruota siunčiant]',
      suma: Number(amountEur) > 0 ? `${Number(amountEur).toFixed(2)} Eur` : '',
      saskaitosNumeris: '[sąskaitos numeris]',
      data: new Date().toISOString().slice(0, 7)
    };
    return previewPreinvoiceMessage(messageTemplate, previewVars);
  }, [messageTemplate, selectedRecipients, amountEur]);

  const handleSendConfirmed = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError('');
    try {
      await sendPreinvoices({
        amountEur: Number(amountEur),
        recipients: selectedRecipients,
        isTest,
        messageTemplate
      });
      setSelectedRecipientIds([]);
      setRecipientSearch('');
      setMessageTemplate(PREINVOICE_DEFAULT_MESSAGE_TEMPLATE);
      setMessageOpen(false);
      setOpen(false);
    } catch (err) {
      setError(err.message || 'Nepavyko išsiųsti išankstinių sąskaitų.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (docId, status) => {
    setStatusUpdatingId(docId);
    try {
      await updatePreinvoiceStatus(currentYear, docId, status);
    } catch (err) {
      setError(err.message || 'Nepavyko pakeisti statuso.');
    } finally {
      setStatusUpdatingId('');
    }
  };

  if (!currentUser || !isAdminUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Tik administratorius gali pasiekti sąskaitų generavimą.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 700, letterSpacing: '-0.02em' }}>
          Sąskaitos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Šiame puslapyje galite paleisti sąskaitų testus ir siųsti išankstines sąskaitas klientams.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2.5, mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Išankstinė sąskaita klientams
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Parinkite gavėjus ir sumą. Kiekvienam gavėjui sugeneruojamas PDF, išsaugomas Firebase Storage bei įrašomas į preinvoices kolekciją.
          </Typography>
          <Button variant="contained" onClick={openModal}>
            Siųsti išankstinę sąskaitą-faktūrą
          </Button>
        </Paper>

        <Stack spacing={2.5}>
          {INVOICE_TEST_ROUTES.map((route) => (
            <Paper key={route.to} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {route.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {route.description}
              </Typography>
              <Button component={RouterLink} to={route.to} variant="outlined">
                {route.buttonLabel}
              </Button>
            </Paper>
          ))}
        </Stack>

        <Paper sx={{ p: 2.5, mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Naujausios išankstinės sąskaitos ({currentYear})
          </Typography>
          <Stack spacing={1}>
            {recentPreinvoices.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Dar nėra įrašų.
              </Typography>
            )}
            {recentPreinvoices.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.clientName || 'Klientas'} ({item.clientEmail})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.invoiceNumber || item.id} | {Number(item.amountEur || 0).toFixed(2)} Eur
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label={item.status || '—'} color={item.status === 'sendError' ? 'error' : item.status === 'Payed' ? 'success' : 'default'} />
                    <Select
                      size="small"
                      value={item.status || 'pending payment'}
                      disabled={statusUpdatingId === item.id}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                    >
                      {PREINVOICE_STATUSES.map((st) => (
                        <MenuItem key={st} value={st}>{st}</MenuItem>
                      ))}
                    </Select>
                    {item.pdfUrl && (
                      <Button size="small" href={item.pdfUrl} target="_blank" rel="noopener noreferrer">
                        PDF
                      </Button>
                    )}
                  </Box>
                </Box>
                {item.sendError && (
                  <Typography variant="caption" color="error.main">
                    sendError: {item.sendError}
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Container>

      <Dialog
        open={open}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>Siųsti išankstinę sąskaitą-faktūrą</DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
            <TextField
              type="number"
              label="Suma (Eur)"
              value={amountEur}
              onChange={(e) => setAmountEur(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />}
                label="Test (nemažina sąskaitų sekos)"
              />
            </FormGroup>
            <Divider />
            <Typography variant="subtitle2">Klientų sąrašas</Typography>
            <TextField
              size="small"
              label="Ieškoti pagal vardą arba el. paštą"
              value={recipientSearch}
              onChange={(e) => setRecipientSearch(e.target.value)}
              fullWidth
            />
            <List
              dense
              subheader={<li />}
              sx={{
                flex: 1,
                minHeight: 160,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0,
              }}
            >
              {selectedRecipients.length > 0 && (
                <li>
                  <ul style={{ padding: 0 }}>
                    <ListSubheader
                      sx={{
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                        fontWeight: 700,
                        lineHeight: '32px',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      Pasirinkti ({selectedRecipients.length})
                    </ListSubheader>
                    {selectedRecipients.map((r) => (
                      <ListItem
                        key={`sel-${r.id}`}
                        disablePadding
                        sx={{ bgcolor: 'action.selected' }}
                        secondaryAction={(
                          <Checkbox
                            edge="end"
                            checked
                            tabIndex={-1}
                            disableRipple
                            onChange={() => toggleRecipient(r.id)}
                          />
                        )}
                      >
                        <ListItemButton
                          onClick={() => toggleRecipient(r.id)}
                          sx={{ pl: 1.5, pr: 6 }}
                        >
                          <ListItemText
                            primary={r.fullName || 'Be vardo'}
                            secondary={r.email}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </ul>
                </li>
              )}
              <li>
                <ul style={{ padding: 0 }}>
                  <ListSubheader
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      fontWeight: 600,
                      lineHeight: '32px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {recipientSearch.trim() ? 'Paieškos rezultatai' : 'Visi klientai'}
                  </ListSubheader>
                  {unselectedFilteredRecipients.map((r) => (
                    <ListItem
                      key={r.id}
                      disablePadding
                      secondaryAction={(
                        <Checkbox
                          edge="end"
                          checked={false}
                          tabIndex={-1}
                          disableRipple
                          onChange={() => toggleRecipient(r.id)}
                        />
                      )}
                    >
                      <ListItemButton
                        onClick={() => toggleRecipient(r.id)}
                        sx={{ pl: 1.5, pr: 6 }}
                      >
                        <ListItemText
                          primary={r.fullName || 'Be vardo'}
                          secondary={r.email}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {unselectedFilteredRecipients.length === 0 && (
                    <ListItem>
                      <ListItemText
                        primary={
                          recipientSearch.trim()
                            ? 'Nieko nerasta pagal įvestą paiešką.'
                            : 'Daugiau klientų nėra.'
                        }
                      />
                    </ListItem>
                  )}
                </ul>
              </li>
            </List>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={closeModal} disabled={sending}>Atšaukti</Button>
          <Button variant="contained" disabled={!canGoToMessage} onClick={goToMessageStep}>
            Kitas
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={messageOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ flexShrink: 0 }}>El. laiško žinutės peržiūra ir redagavimas</DialogTitle>
        <DialogContent
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Žinutę galite redaguoti laisvai. Žemiau išvardinti vietos žymekliai (placeholderiai) bus automatiškai pakeisti kiekvienam gavėjui prieš siunčiant.
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Galimi žymekliai:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                {PREINVOICE_TEMPLATE_PLACEHOLDERS.map((p) => (
                  <li key={p.key}>
                    <Typography variant="caption">
                      <code>{p.key}</code> — {p.description}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
            <TextField
              label="Žinutės tekstas"
              value={messageTemplate}
              onChange={(e) => setMessageTemplate(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Peržiūra {selectedRecipients[0] ? `(pirmas gavėjas: ${selectedRecipients[0].fullName || selectedRecipients[0].email})` : ''}
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'background.default',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  maxHeight: 220,
                  overflow: 'auto',
                }}
              >
                {messagePreview}
              </Paper>
            </Box>
            <Button
              size="small"
              onClick={() => setMessageTemplate(PREINVOICE_DEFAULT_MESSAGE_TEMPLATE)}
              disabled={messageTemplate === PREINVOICE_DEFAULT_MESSAGE_TEMPLATE || sending}
              sx={{ alignSelf: 'flex-start' }}
            >
              Atstatyti standartinį tekstą
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button onClick={goBackToSelectionStep} disabled={sending}>Atgal</Button>
          <Button
            variant="contained"
            disabled={!canSubmit || !messageTemplate.trim()}
            onClick={() => setConfirmOpen(true)}
          >
            Siųsti
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Patvirtinimas</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Patvirtinkite siuntimą: {selectedRecipients.length} gavėjų, suma {Number(amountEur || 0).toFixed(2)} Eur, test režimas: {isTest ? 'Taip' : 'Ne'}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>Atšaukti</Button>
          <Button variant="contained" onClick={handleSendConfirmed} disabled={!canSubmit}>
            Patvirtinti ir siųsti
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
